// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import myw from 'myWorld/base/core';
import 'myWorld/layers/overlay';

myw.LayerManager = myw.MywClass.extend({
    /**
     * @class Handles obtaining layer configuration and instantiating those layers
     * @param  {GeoMapControl}  map
     * @constructs
     */
    initialize(map, options) {
        this.app = map.app;
        this.options = options = options || {};

        /** @type {GeoMapControl} */
        this.map = map;
        /** @type {System} */
        this.system = this.app.system;

        /** holds the layer objects. Keyed on layer id
         * @type {Object<string, Layer>} */
        this.layers = {};

        /** Holds the layers, layer groups  and layer group items in the user's layer list.
         * It is maintained by any layer additions or deletions on the map
         * @type {array<layerListItem>} */
        this.layerList = [];

        this._availableLayers = null;
        this._createLayerHooks = [];

        map.app.on('featureCollection-modified', this.handleFeatureChanges, this);
    },

    /**
     * Ensures the initial layer list is determined and visible layers are added to the map.
     * If the initial list hasn't been specified via a call to setLayerList (usually by LayerControl from its saved state),
     * all available layers are added as the initial list
     * @return {Promise}
     */
    ensureInitialLayers() {
        if (this._initialLayersPromise) return this._initialLayersPromise;

        let defaultListPromise;
        if (!_.isEmpty(this.layerList)) defaultListPromise = Promise.resolve();
        else
            defaultListPromise = this.getDefaultLayerList().then(layerList => {
                //layer list may have been set meanwhile, so we check again before setting it
                this.setLayerList(layerList, false);
            });

        this._initialLayersPromise = defaultListPromise.then(() => {
            if (this.initialVisibleLayers) this.setLayersVisibility(this.initialVisibleLayers);
        });

        return this._initialLayersPromise;
    },

    /**
     * Obtains a layer list with all available layers for the current user and application.
     * @return {Promise<Array<layerListItem>>}
     */
    getDefaultLayerList() {
        return this.getLayerListLayersAndGroups().then(list => {
            const layerArray = _.union(list.layers, list.layerGroups);
            return _.sortBy(layerArray, 'layer_name');
        });
    },

    /**
     * Sets the given layerList as the current layer list and adds the corresponding layers to the map
     * @param {Array<layerListItem>} layerList
     * @param {boolean} overwrite If false, it won't overwrite an existing (non-empty) list
     */
    async setLayerList(layerList, overwrite = true) {
        const applicationName = this.map.app.name;
        const result = await this.system.getStartupInfo(applicationName);

        if (!_.isEmpty(this.layerList) && !overwrite) return;

        //Remove any existing layers from the map and add the default layers
        if (this.layerList && this.layerList.length > 0) {
            this.removeAllLayers();
        }

        for (let i = 0; i < result.layers.length; ++i) {
            if (result.layers[i].thumbnail)
                result.layers[i].thumbnail = myw.baseUrl + result.layers[i].thumbnail;
        }

        this.layerList = this._setLayerDefs(layerList, result.layers, result.layerGroups);
        this._addLayersToMap(this.layerList);
    },

    /**
     * returns a new layerlist with specs and without inacessible layers
     * also  excludes layers or groups that may have had their definitions removed from the database
     * @private
     */
    _setLayerDefs(layerList, layerDefs, layerGroups) {
        const updateLayerGroup = (layerList, layerGroup) => {
            //returns a new layer list with the items from layerGroup and the state available in layerList
            //means new items in the group become available and removed items also disapear from the list
            //if layerGroup is missing, will return an empty array (empty layer list)
            const layerNames = (layerGroup && layerGroup.layers) || [];
            const seq = layerList[0] && layerList[0].sequence;
            return layerNames.map((layerName, index) => {
                const existingItem = _.findWhere(layerList, { layer_name: layerName });
                return {
                    type: 'layer_group_item',
                    sequence: seq, //ENH: this shouldn't be necessary
                    subsequence: index + 1, //ENH: this shouldn't be necessary
                    layer_name: layerName,
                    turned_on: (existingItem && existingItem.turned_on) || false
                };
            });
        };

        _.each(layerList, item => {
            if (item.type == 'layer_group') {
                const groupDef = _.findWhere(layerGroups, { name: item.layer_name });
                item.subLayers = updateLayerGroup(item.subLayers, groupDef);
                item.subLayers = this._setLayerDefs(item.subLayers, layerDefs);

                if (item.subLayers.length === 0) delete item.subLayers;
                //removes the group
                else {
                    item.description = groupDef.description;
                    item.thumbnail = groupDef.thumbnail;
                    item.display_name = groupDef.display_name;
                }
            } else {
                item.layerDef = _.findWhere(layerDefs, { id: item.layer_name });
            }
        });

        //exclude layers or groups that may have had their definitions removed from the database
        return _.filter(layerList, item => item.layerDef || item.subLayers);
    },

    getLayerListLayersAndGroups() {
        return this.getAvailableLayersAndGroups().then(layersAndGroups => {
            //make the result look like a layer list

            const layers = _.map(layersAndGroups.layers, layerDef =>
                this._createLayerListLayerFromDef(layerDef)
            );

            const layerGroups = _.map(layersAndGroups.layerGroups, groupDef =>
                this._createLayerListGroupFromDef(groupDef, layers)
            );

            const groupedLayerNames = _.flatten(_.pluck(layersAndGroups.layerGroups, 'layers')),
                //We only show the layers that are not part of any groups
                layersToShow = _.filter(
                    layers,
                    layer => !_.contains(groupedLayerNames, layer.layer_name)
                );

            return { layers: layersToShow, layerGroups: layerGroups };
        });
    },

    /**
     * Creates a layerListItem object using the layerDef
     * @param  {layerDefinition} layerDef
     * @return {layerListLayer}            The layer object in layerListItem format
     * @private
     */
    _createLayerListLayerFromDef(layerDef) {
        return {
            type: 'layer',
            layer_name: layerDef.id,
            turned_on: true, //By default we always want the layer to be turned on, esp. when we add a new layer or group
            layerDef: layerDef
        };
    },

    /**
     * Creates a layerListGroup object using the groupDef
     * @param  {object} groupDef
     * @param  {array<layerDefinition>} layerDefs
     * @return {object}                 The layerListGroup object in the layerListGroup format
     * @private
     */
    _createLayerListGroupFromDef(groupDef, layers) {
        const subLayersArray = [];
        let subLayer;

        //Create layers from the subLayerNames
        _.each(
            groupDef.layers,
            subLayerName => {
                //Only the subLayers accessible to the application will be added to the subLayerArray
                subLayer = _.findWhere(layers, { layer_name: subLayerName });
                if (subLayer) subLayersArray.push(subLayer);
            },
            this
        );

        return {
            type: 'layer_group',
            layer_name: groupDef.name,
            display_name: groupDef.display_name,
            turned_on: true, //turned on by default
            description: groupDef.description,
            exclusive: groupDef.exclusive,
            thumbnail: groupDef.thumbnail,
            subLayers: subLayersArray
        };
    },

    /**
     * Obtain all overlay layer definitions available to the current user & application
     * @return {Promise<Array<layerDefinition>>}
     */
    getAvailableLayers() {
        if (!this._availableLayers) {
            this._availableLayers = this.getAvailableLayersAndGroups().then(
                availableLayersAndGroups => availableLayersAndGroups.layers
            );
        }
        return this._availableLayers;
    },

    /**
     * Obtain all overlay layer definitions and layer group definitions available to the current user & application
     * @return {Promise<object>} Object with available layers and layerGroups
     */
    getAvailableLayersAndGroups() {
        return this.system.getStartupInfo(this.app.name).then(results => {
            const overlays = _.filter(results.layers, layer => layer.category == 'overlay');
            const layerGroups = this._filterAvailableLayerGroups(results.layerGroups, overlays);

            return { layers: overlays, layerGroups: layerGroups };
        });
    },

    /**
     * Removes the layerGroups that don't have any accessible layers from the list returned by the API
     * @param  {array<object>} layerGroups List of Layer groups returned by the API (all defined layer groups)
     * @param  {array<object>} layers      List of Layers accessible to the application
     * @return {array<object>}             List of layerGroups with accessible layers
     * @private
     */
    _filterAvailableLayerGroups(layerGroups, layers) {
        const overlays = _.filter(layers, layer => !layer.hasOwnProperty('owner'));
        const layerNames = _.pluck(overlays, 'name');
        const availableLayerGroups = [];

        _.each(layerGroups, group => {
            //If any of the group layers are accessible, add it to the list of availableLayerGroups
            if (_.intersection(group.layers, layerNames).length > 0) {
                availableLayerGroups.push(group);
            }
        });
        return availableLayerGroups;
    },

    /**
     * Return the layer with the provided name.
     * @param {string} id Identifier of the layer to obtain. Name for system layers
     * @return {Layer}
     */
    getLayer(id) {
        return this.layers[id];
    },

    /**
     * Returns a layer or a layer-group with the provided name in the layerList
     * @param  {string}  layerItemName Name of the layer or group to obtain
     * @param  {Boolean} isGroup       [description]
     * @return {layerListLayer|layerListGroup}                [description]
     */
    getLayerFromLayerList(layerItemName, isGroup) {
        const type = isGroup ? 'layer_group' : 'layer',
            layer = _.findWhere(this.layerList, { type: type, layer_name: layerItemName });
        return layer;
    },

    /**
     * Obtain the layers that represent a given feature type
     * @param  {string} featureType
     * @return {Array<Layer>}
     */
    getLayersForFeatureType(featureType) {
        const layers = [];

        _.each(this.layers, layer => {
            const layerFeatureTypes = _.pluck(layer.layerDef.feature_types, 'name');
            if (_.contains(layerFeatureTypes, featureType)) {
                layers.push(layer);
            }
        });
        return layers;
    },

    /**
     * Instantiate layers and add them to the map
     * @param {Array<layerListItem>} layerList     List of layer list items
     * @param {boolean} [active=true]              Whether the list is active/on
     * @param {boolean} [fireEvent=true]           Whether to fire the 'overlays-changed' event
     * @private
     */
    _addLayersToMap(layerList, active = true, fireEvent = true) {
        // add layer to map, and to the layer datasourceConfig
        _.each(layerList, layerListItem => {
            if (layerListItem.type == 'layer_group') {
                //add sub layers
                this._addLayersToMap(layerListItem.subLayers, layerListItem.turned_on, false);

                return; //not a layer, continue
            }

            const layerDef = layerListItem.layerDef;
            let visible;

            if (this.initialVisibleLayers) {
                visible = _.contains(this.initialVisibleLayers, layerDef.code);
            } else {
                visible = active && layerListItem.turned_on === true;
            }

            try {
                this._addLayerFromDef(layerDef, visible);
            } catch (e) {
                console.log('Adding layer ', layerListItem.layer_name, e);
            }
        });

        if (fireEvent) this.map.app.fire('overlays-changed');
    },

    /**
     * Instantiates a layer from a definition and adds it to the map
     * @param {layerDefinition} layerDef        A layer definition
     * @param {boolean}         setChecked      Whether the layer should be "checked", i.e. visible when enabled
     * @return {Layer} The new and added layer wrapper
     */
    addLayerFromDef(layerDef, setChecked) {
        //ENH: should return a promise for when the layer has effectively been added
        const layer = this._addLayerFromDef(layerDef, setChecked);

        //update layerList, making sure duplicates are not added
        if (!_.findWhere(this.layerList, { type: 'layer', layer_name: layerDef.id })) {
            const layerListLayer = this._createLayerListLayerFromDef(layerDef);
            this.layerList.push(layerListLayer);
            this.map.app.fire('overlays-changed', { map: this.map, layer: layer });
        }
        return layer;
    },

    /**
     * Instantiates a layer from a definition and adds it to the map
     * @param {layerDefinition} layerDef        A layer definition
     * @param {boolean}         setChecked      Whether the layer should be "checked", i.e. visible when enabled
     * @return {Layer} The new and added layer wrapper
     * @private
     */
    _addLayerFromDef(layerDef, setChecked) {
        let layer = this.layers[layerDef.id];
        if (!layer) {
            layer = this.createLayer(layerDef);
        }
        if (layer) {
            // add the layers to the leaflet map
            this.addLayerToMap(layer, setChecked);
        }
        return layer;
    },

    /**
     * Adds a group to the layerList and its subLayers to the map.
     * Fires the "overlays-changed" event.
     * @param {layerListGroup} group  Object with layer group properties
     * @param {array<layerDefinition>} subLayersArray Array of layers in the group
     */
    addLayerGroupToList(group) {
        if (!_.findWhere(this.layerList, { type: 'layer_group', layer_name: group.layer_name })) {
            if (group.exclusive) {
                //Since its an exclusive group
                //Make all the sub layers other than the first one turned off
                //So only the first layer is turned on by default
                group.subLayers = _.map(group.subLayers, (subLayer, index) => {
                    if (index !== 0) subLayer.turned_on = false;
                    return subLayer;
                });
            }
            this.layerList.push(group);
            this._addLayersToMap(group.subLayers);
        }
    },

    /**
     * Adds a layer to map, first checking if it should be available
     * @param {Layer} layer A record from table layer
     * @param {boolean}         setChecked  Whether the layer should be "checked", i.e. visible when enabled
     */
    addLayerToMap(layer, setChecked) {
        const layerDef = layer.layerDef;
        this.layers[layerDef.id] = layer;
        this.setLayerChecked(layerDef.id, setChecked);
    },

    /**
     * Creates a layer as specified by layerDef
     * @param  {layerDefinition}    layerDef        A record of table layer
     * @return {Layer}          The newly created leaflet layer
     */
    createLayer(layerDef) {
        let layer, datasource;

        datasource = this.app.getDatasource(layerDef.datasource);

        layerDef.options = _.extend(
            {},
            {
                maxZoom: layerDef.max_scale,
                minZoom: layerDef.min_scale,
                attribution: layerDef.attribution,
                opacity: 1.0 - layerDef.transparency / 100,
                textPaneName: this.options.textPaneName,
                schema: layerDef.extraOptions && layerDef.extraOptions.schema
            },
            layerDef.options
        );

        _.each(this._createLayerHooks, hook => {
            hook(layerDef);
        });

        layer = new myw.Overlay(datasource, layerDef, this.map);

        return layer;
    },

    /**
     * Saves and makes available a given private layer definition
     * @param {privateLayerDef} privateLayerDef
     * @return {promise}
     */
    async savePrivateLayerDef(privateLayerDef) {
        privateLayerDef = await this.system.savePrivateLayer(privateLayerDef);

        //make datasource available
        const datasourceDef = {
            name: privateLayerDef.id,
            external_name: `${privateLayerDef.name} (${privateLayerDef.owner})`,
            owner: privateLayerDef.owner,
            ...privateLayerDef.datasource_spec
        };
        this.app.database.saveUserDatasource(datasourceDef);

        const startupInfo = await this.system.getStartupInfo(this.app.name);

        //make layer available
        //do startupinfo post-processing so it's stored in same structure
        privateLayerDef.datasource = privateLayerDef.id;
        _.extend(privateLayerDef, privateLayerDef.spec);
        delete privateLayerDef.spec;

        const existingDef = _(startupInfo.layers).findWhere({ id: privateLayerDef.id });
        if (existingDef) _.extend(existingDef, privateLayerDef);
        else startupInfo.layers.push(privateLayerDef);

        return privateLayerDef;
    },

    /**
     * Deletes a private layer definition
     * @param {string} id
     * @return {promise}
     */
    async deletePrivateLayerDef(id) {
        await this.system.deletePrivateLayer(id);

        const startupInfo = await this.system.getStartupInfo(this.app.name);
        const index = _(startupInfo.layers).findIndex({ id: id });
        startupInfo.layers.splice(index, 1);
        return true;
    },

    /**
     * get an overlay definition with the one character code
     * @param  {Array<layerDefinition>} overlayDefs
     * @param  {string} layerCode
     * @return {object}          a myWorld overlay definition object
     * @private
     */
    _getOverlayDefWithCode(overlayDefs, layerCode) {
        return _.findWhere(overlayDefs, { code: layerCode });
    },

    /**
     * Removes all layers from the map before adding default layers
     */
    removeAllLayers() {
        this.layerList = {};
        for (const layerName in this.layers) {
            this._removeLayer(layerName);
        }
    },

    /**
     * Removes the layer from the layerList
     * @param  {string} layerName the layer's name
     */
    removeLayerFomList(layerName) {
        this.layerList = _.without(this.layerList, this.getLayerFromLayerList(layerName));
        this._reSequenceLayerList();

        let isUnique = true;
        _.each(this.layerList, layerItem => {
            if (
                layerItem.subLayers &&
                _.findWhere(layerItem.subLayers, { layer_name: layerName })
            ) {
                isUnique = false;
                return;
            }
        });

        if (isUnique) {
            this._removeLayer(layerName);
        }
        this.map.app.fire('overlays-changed', { map: this.map });
    },

    /**
     * Removes layer from map and from the myWorld layers object
     * @param  {string} layerName the layer's name
     * @private
     */
    _removeLayer(layerName) {
        const layer = this.getLayer(layerName);
        //if the layer exists, remove it from the map and the layer object
        if (layer) layer.setVisibility(false);

        delete this.layers[layerName];
    },

    /**
     * Removes the layer group from the map and the layerList
     * @param  {string} groupName  The group's name
     */
    removeGroup(groupName) {
        const groupInList = this.getLayerFromLayerList(groupName, true);
        this.layerList = _.without(this.layerList, groupInList);
        this._reSequenceLayerList();

        //Only remove the layers from map if the same layer is not present in a group in the current list
        _.each(groupInList.subLayers, subLayer => {
            const subLayerName = subLayer.layer_name;
            let isUnique = true;

            _.each(this.layerList, layerItem => {
                //check if its unique
                if (
                    layerItem.layer_name == subLayerName ||
                    (layerItem.subLayers &&
                        _.findWhere(layerItem.subLayers, { layer_name: subLayerName }))
                ) {
                    isUnique = false;
                    return;
                }
            });
            if (isUnique) this._removeLayer(subLayerName);
        });
    },

    /**
     * Updates the layer groups expanded property in the layerList
     * @param  {string}  groupName Name of the layer group
     * @param  {boolean} expanded  Refers to the state of the layer group in the layerControl
     */
    updateGroupExpandedState(groupName, expanded) {
        _.each(this.layerList, item => {
            if (item.layer_name === groupName) item.expanded = expanded;
        });
    },

    _reSequenceLayerList() {
        let sequence = 1;
        _.each(this.layerList, layer => {
            layer.sequence = sequence;
            if (layer.type === 'layer_group') {
                _.each(layer.subLayers, subLayer => {
                    subLayer.sequence = sequence;
                });
            }
            sequence++;
        });
    },

    /**
     * Removes all layers from the map
     */
    removeLayers() {
        for (const layerName in this.layers) {
            this.removeLayerFomList(layerName);
        }
    },

    /**
     * Returns the currently visible overlay layers
     * The layer has to be turned on and the configuration for the layer has to match the provided zoom level.
     * If a layer does not have a code then there is no entry for it in the list.
     * @return {array<Layer>}
     */
    getVisibleLayers(zoomLevel) {
        return _.filter(this.layers, layer => layer.isVisibleAtZoom(zoomLevel) && layer.isVisible);
    },

    /**
     * Returns an array of ids describing the currently ON overlay maps
     * The layer has to be turned on.
     * If a layer does not have a code then there is no entry for it in the list.
     * @return {string[]} List with ids of the layers
     */
    getCurrentLayerIds() {
        const ids = [];

        // For all of the map type collections.
        // For all of the overlays within a collection.
        _.each(this.layers, layer => {
            const code = layer.getCode();
            // Make sure that it is the overlays that the user had turned on
            if (layer.isChecked && code !== '') {
                ids.push(code);
            }
        });
        return ids;
    },

    /**
     * Returns a description/tooltip to add to the layer item and the sublayer item in the layer control and add layer control
     * Includes the datasource external name/name for external datasources
     * @param  {layerDefinition} layerDef  Layer to be added to the layer control
     * @return {string}                    Tooltip for the layer item
     */
    getLayerItemDescription(layerDef) {
        const layer = this.layers[layerDef.id] || this.createLayer(layerDef);
        let description = layerDef.description || '';
        const datasourceExternalName = layer.datasource.getExternalName();
        const isExternalDatasource = layerDef.datasource !== 'myworld';

        if (isExternalDatasource && datasourceExternalName) {
            description += ` (${datasourceExternalName})`;
        }

        return description;
    },
    /**
     * Sets the desired visibility of a layer (the "checked" state)
     * Triggers overlayState-changed event (even if it was already in the desired state)
     * @param  {string|Layer}   layerId       The layer or the name of the layer
     * @param  {boolean}            checked         desired visibility of the overlay
     */
    setLayerChecked(layerId, checked) {
        const layer = this.getLayer(layerId);
        layer.setVisibility(checked);

        //Update layer item in the layerList
        const layerItem = this.getLayerFromLayerList(layerId);
        if (layerItem) layerItem.turned_on = checked;
    },
    /**
     * Sets the desired visibility of a group (the "checked" state)
     * @param {string}   groupName   Name of the layer group
     * @param {bookean}  checked     Desired visibility of the group
     */
    setGroupChecked(groupName, checked) {
        const group = _.findWhere(this.layerList, { type: 'layer_group', layer_name: groupName });
        this.getLayerFromLayerList(groupName, true).turned_on = checked;

        _.each(group.subLayers, subLayer => {
            // Only set any other layer with this name as checked since we want to preserve the turned_on state of the subLayers
            if (subLayer.turned_on) this.setLayerChecked(subLayer.layer_name, checked);
        });
    },
    /**
     * Sets the desired visibility of a layer that belongs in a group (the "checked" state)
     * @param {string}   groupName     Name of the layer group
     * @param {string}   subLayerName  Name of the layer
     * @param {bookean}  checked       Desired visibility of the layer
     * @param {bookean}  markOnly      Only update the checked status, don't make it visible
     */
    setSubLayerChecked(groupName, subLayerName, checked, markOnly) {
        const layer = this.getLayer(subLayerName);
        if (markOnly) layer.setCheckedStatus(checked);
        else layer.setVisibility(checked);

        //Update sub layer in the layerList
        this.updateSubLayerInLayerList(groupName, subLayerName, checked);
    },

    /**
     * Updates the turned_on flag of the sub-layer in the layerList
     * @param {string}   groupName     Name of the layer group
     * @param {string}   subLayerName  Name of the layer
     * @param {bookean}  checked       Desired visibility of the layer
     */
    updateSubLayerInLayerList(groupName, subLayerName, checked) {
        const group = _.findWhere(this.layerList, { type: 'layer_group', layer_name: groupName });
        _.findWhere(group.subLayers, { layer_name: subLayerName }).turned_on = checked;
    },

    /**
     * Sets the visibility of the layers in the application
     *
     * @param  {string[]} visibleLayers List of ids of the layers that should be visible
     */
    setLayersVisibility(visibleLayers) {
        if (!_.isEmpty(this.layers)) {
            this.getAvailableLayers().then(availableLayerDefs => {
                //check if there are layers that were requested but weren't added and are available
                const layerCodes = _.map(this.layers, layer => layer.getCode());

                const codesNotInLayerList = _.difference(visibleLayers, layerCodes);
                let layersChanged = false;

                this._setGroupsVisible(visibleLayers);

                //add new layers to the map
                _.each(codesNotInLayerList, layerCode => {
                    //allow for forward slash in url
                    if (layerCode && layerCode != '/') {
                        const overlayDef = this._getOverlayDefWithCode(
                            availableLayerDefs,
                            layerCode
                        );
                        if (overlayDef) {
                            layersChanged = true;
                            this.addLayerFromDef(overlayDef, true);
                        }
                    }
                });

                if (layersChanged) {
                    this.map.app.fire('overlays-changed');
                }

                for (const name in this.layers) {
                    const layer = this.layers[name],
                        visible = $.inArray(encodeURI(layer.getCode()), visibleLayers) > -1;
                    this.setLayerChecked(name, visible);
                }
            });
            return null;
        } else {
            //layers haven't been added yet - save the information to be used when they are added
            this.initialVisibleLayers = visibleLayers;
        }
    },

    /**
     * Since the layer groups are not included in the mapLink and bookmarks.
     * This method turns the group with a visible layer ON.
     * @param {array<string>} layerCodes Layer codes of the visible layers
     * @private
     */
    _setGroupsVisible(layerCodes) {
        const visibleLayers = _(this.layers).filter(layer =>
            layerCodes.includes(layer.layerDef.code)
        );
        const visibleLayerNames = visibleLayers.map(layer => layer.layerDef.name);

        _.each(
            this.layerList,
            layerItem => {
                if (
                    layerItem.type === 'layer_group' &&
                    _.find(layerItem.subLayers, subLayer =>
                        _.contains(visibleLayerNames, subLayer.layer_name)
                    )
                ) {
                    layerItem.turned_on = true;
                }
            },
            this
        );
    },

    /**
     * Handler for feature collection modified event
     * Informs relevant layers that features have changed, so they can refresh
     *
     */
    handleFeatureChanges(e) {
        const layers = this.getLayersForFeatureType(e.featureType);

        _.forEach(layers, layer => {
            layer.featureModified(e.changeType, e.feature);
        });
    },

    /**
     * Redraws each visible layer
     */
    redraw() {
        _.forEach(this.layers, layer => {
            if (layer.isVisible) layer.update();
        });
    },

    getState() {
        const itemStateFunc = item => {
                const itemState = _.pick(item, [
                    'type',
                    'sequence',
                    'subsequence',
                    'layer_name',
                    'turned_on',
                    'exclusive',
                    'expanded'
                ]);
                if (item.type == 'layer_group') {
                    itemState.subLayers = _.map(item.subLayers, itemStateFunc);
                }
                return itemState;
            },
            layerList = _.map(this.layerList, itemStateFunc);

        return layerList;
    },

    /**
     * Add a hook function which will be called just before a layer is created
     * @param {function} hook   The hook function
     * The function will be passed a layerDefinition object which is used to
     * construct the layer.
     */
    addCreateLayerHook(hook) {
        this._createLayerHooks.push(hook);
    }
});

export default myw.LayerManager;
